// ============================================================
// v11 §2D (BOLT-024) — hold-to-talk voice capture. MediaRecorder
// behind a tiny start/stop surface: press starts a capped take,
// release yields a compressed data URL ready for `send_voice`.
// Everything is capability-guarded — no mic / no MediaRecorder /
// a denied prompt all degrade to null (the button stays inert).
// ============================================================

/** Best supported container: Safari records audio/mp4 (AAC), Chromium
 *  webm/opus — both are accepted server-side (voice.ts sniff). */
function pickMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

export function voiceSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && pickMime() !== null;
}

export interface VoiceTake {
  /** Resolves with the recorded data URL (null: empty/denied/failed). */
  stop: () => Promise<string | null>;
  cancel: () => void;
}

/** Start recording (asks for the mic on first use). `maxMs` hard-stops the
 *  take server-parity-style so a stuck finger can't run past the cap. */
export async function startVoiceTake(maxMs = 10_000): Promise<VoiceTake | null> {
  const mime = pickMime();
  if (!mime || !navigator.mediaDevices?.getUserMedia) return null;
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    return null; // denied — the button simply does nothing
  }
  const rec = new MediaRecorder(stream, { mimeType: mime });
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  const done = new Promise<void>((res) => {
    rec.onstop = () => res();
  });
  rec.start();
  const killer = setTimeout(() => {
    if (rec.state === 'recording') rec.stop();
  }, maxMs);

  const teardown = () => {
    clearTimeout(killer);
    for (const t of stream.getTracks()) t.stop();
  };

  return {
    stop: async () => {
      if (rec.state === 'recording') rec.stop();
      await done;
      teardown();
      const blob = new Blob(chunks, { type: mime.split(';')[0] });
      if (blob.size < 128) return null; // a tap, not a take
      return await new Promise<string | null>((res) => {
        const r = new FileReader();
        r.onload = () => res(typeof r.result === 'string' ? r.result : null);
        r.onerror = () => res(null);
        r.readAsDataURL(blob);
      });
    },
    cancel: () => {
      if (rec.state === 'recording') rec.stop();
      teardown();
    },
  };
}
